import { Divider, MenuItem } from "@material-ui/core";
import React from "react";
import styled, { css } from "styled-components";

import { ICollaborationInvite, IWorkspacePermission, WorkspacePermissionSubjectType, WorkspaceUserRole } from "common/interfaces";
import { Button } from "js/Components/Button";
import { Dropdown } from "js/Components/Dropdown";
import PresentationPermissionsController, { PresentationPermissionsControllerState } from "js/controllers/PresentationPermissionsController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import Avatar, { FolderAvatar, UserGroupAvatar } from "js/react/components/Avatar";
import { ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ResendInviteDialog } from "js/react/components/InviteInput/ResendInviteDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/ManageTeamFolderMembersDialog";
import TransferOwnershipDialog from "js/react/views/PresentationSettings/dialogs/TransferOwnershipDialog";
import GuestBadge from "js/react/components/GuestBadge";
import { t } from "js/i18n";
const logger = getLogger(LogGroup.PERMISSIONS);

const Container = styled.div<{ $showFirstDivider?: boolean }>`
    margin-left: -20px;
    width: calc(100% + 40px);
    color: #333;
    position: relative;
    overflow-y: auto;
    min-height: 50px;

    ${props => !props.$showFirstDivider && css`
        > *:first-child {
            border-top: none;
        }
    `}
`;

const ResendInviteButton = styled(Button)`
    &&& {
        opacity: 0;
        transition: opacity 300ms;
    }
`;

const OpenManageFolderButton = styled(Button)`
    &&& {
        opacity: 0;
        transition: opacity 300ms;
        text-wrap: nowrap;
    }
`;

const PermissionRowContainer = styled.div`
    display: grid;
    grid-template-columns: 3fr 1fr 1fr 5fr 1fr;
    align-items: center;
    height: 54px;

    /* 20px side margin = inset divider; 10px pad keeps content at 30px from card edge */
    margin: 0 20px;
    padding: 0 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    &:hover {
      background: #f0f0f0;

      ${ResendInviteButton} {
        opacity: 1;
      }

      ${OpenManageFolderButton} {
        opacity: 1;
      }

      .MuiSelect-root {
        background: #f0f0f0;
      }
    }
`;

const PermissionRowNameContainer = styled.div`
    white-space: nowrap;
    margin: 10px;

    .displayName {
        color: #222;
        font-weight: 600;
    }

    .email {
        font-size: 12px;
        color: #666;
        display: block;
    }
`;

const MutedGrayText = styled.div`
    min-width: 84px;
    text-transform: uppercase;
    font-size: 10px;
    color: #555;
`;

function PermissionsList({ permissions, invites, canChangeOwnership, mode = "collaborators", resourceFolders }: PresentationPermissionsControllerState & WorkspaceControllerState & { canChangeOwnership?: boolean, mode?: "collaborators" | "folders" }) {
    const folderGroupIds = new Set(resourceFolders.map(folder => folder.userGroupId));
    const groupPermissions = permissions
        .filter(p => p.subjectType === WorkspacePermissionSubjectType.USER_GROUP)
        .filter(permission => mode === "folders" ? folderGroupIds.has(permission.subjectId) : !folderGroupIds.has(permission.subjectId));

    const usersPermissions = mode === "folders" ? [] : permissions
        .filter(p => p.subjectType === WorkspacePermissionSubjectType.USER)
        .sort((a, b) => (a.owner ? 0 : 100) - (b.owner ? 0 : 100) + (a.write.object ? 0 : 10) - (b.write.object ? 0 : 10) + (a.read.object ? 0 : 1) - (b.read.object ? 0 : 1));

    return (<Container data-test-id="presentation-permissions-list" $showFirstDivider={mode === "folders"}>
        {usersPermissions.map((permission, idx) => (
            <UserPermissionRow key={idx} permission={permission} canChangeOwnership={canChangeOwnership} />
        ))}
        {mode === "collaborators" && invites.map((invite, idx) => (
            <InviteRow key={idx} invite={invite} />
        ))}
        {groupPermissions.map((permission, idx) => (
            <GroupPermissionRow key={idx} permission={permission} />
        ))}
    </Container>);
}

const UserPermissionRow = WorkspaceController.withInitializedState(PresentationPermissionsController.withInitializedState(
    function UserPermissionRow({ permission, permissions, canChangeOwnership, users }: { permission: IWorkspacePermission, canChangeOwnership?: boolean } & PresentationPermissionsControllerState & WorkspaceControllerState) {
        const user = users.find(u => u.uid === permission.subjectId);
        if (!user) {
            return null;
        }

        const selfIsOwner = !!permissions.find(p => p.subjectId === auth().currentUser.uid && p.subjectType === WorkspacePermissionSubjectType.USER)?.owner;

        const handleRemovePermissions = async () => {
            try {
                await withProgressDialog({
                    title: t("Revoking access..."),
                    action: () =>
                        PresentationPermissionsController.removePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId
                            }]
                        })
                });
            } catch (err) {
                logger.error(err, "Failed to revoke access");

                ShowErrorDialog({
                    title: t("Failed to revoke access"),
                    message: err.message
                });
            }
        };

        const handleTransferOwnership = async () => {
            try {
                await withProgressDialog({
                    title: t("Transferring ownership..."),
                    action: async () => {
                        const shouldTransferOwnership = await ShowDialogAsync(TransferOwnershipDialog, {
                            newOwnerEmail: user.email
                        });
                        if (!shouldTransferOwnership) {
                            return;
                        }

                        await PresentationPermissionsController.updatePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId,
                                email: user.email,
                                permissions: {
                                    write: { object: true, permissions: true },
                                    read: { object: true, permissions: true },
                                    owner: true
                                }
                            }]
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Failed to transfer ownership");

                ShowErrorDialog({
                    title: t("Failed to transfer ownership"),
                    message: err.message
                });
            }
        };

        const handleUpdatePermissions = async (update: "edit" | "view") => {
            try {
                await withProgressDialog({
                    title: t("Updating permissions..."),
                    action: async () => {
                        return PresentationPermissionsController.updatePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId,
                                email: user.email,
                                permissions: {
                                    write: { object: update === "edit", permissions: update === "edit" },
                                    read: { object: true, permissions: true },
                                    owner: false
                                }
                            }]
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Failed to update permissions");

                ShowErrorDialog({
                    title: t("Failed to update permissions"),
                    message: err.message
                });
            }
        };

        const handlePermissionDropdownChange = (update: "edit" | "view" | "remove" | "owner") => {
            if (update === "remove") {
                handleRemovePermissions();
            } else if (update === "owner") {
                handleTransferOwnership();
            } else {
                handleUpdatePermissions(update);
            }
        };

        const isSelf = user.uid === auth().currentUser.uid;

        return (<PermissionRowContainer data-test-id="presentation-permissions-list-row-user">
            <FlexBox left>
                <Avatar url={user.photoURL} />
                <PermissionRowNameContainer data-test-id="name">
                    {user.displayName && <span className="displayName">{user.displayName}</span>}
                    <span className="email">{user.email}</span>
                </PermissionRowNameContainer>
                {user.role === WorkspaceUserRole.GUEST && <GuestBadge />}
            </FlexBox>
            <FlexBox left>
                {permission.owner &&
                    <Dropdown
                        value={"owner"}
                        variant="standard"
                        disableUnderline
                        disabled
                        data-test-id="permission-dropdown"
                    >
                        <MenuItem value={"owner"}>{t("Owner")}</MenuItem>
                    </Dropdown>
                }
                {!permission.owner &&
                    <Dropdown
                        value={permission.write.object ? "edit" : "view"}
                        variant="standard"
                        disableUnderline
                        disabled={isSelf}
                        onChange={handlePermissionDropdownChange}
                        data-test-id="permission-dropdown"
                    >
                        <MenuItem value={"edit"}>{t("Can Edit")}</MenuItem>
                        <MenuItem value={"view"}>{t("Can View")}</MenuItem>
                        {selfIsOwner && canChangeOwnership &&
                            <MenuItem value={"owner"}>{t("Make Owner")}</MenuItem>
                        }
                        <Divider />
                        <MenuItem value={"remove"}>{t("Revoke Access")}</MenuItem>
                    </Dropdown>
                }
            </FlexBox>
            <FlexBox left>
                {isSelf &&
                    <MutedGrayText>{t("You")}</MutedGrayText>
                }
            </FlexBox>
            <FlexBox></FlexBox>
            <FlexBox></FlexBox>
        </PermissionRowContainer>);
    }
));

const GroupPermissionRow = WorkspaceController.withInitializedState(
    function GroupPermissionRow({ permission, userGroups, resourceFolders }: { permission: IWorkspacePermission } & WorkspaceControllerState) {
        const group = userGroups.find(g => g.id === permission.subjectId);
        if (!group) {
            return null;
        }

        const folder = resourceFolders.find(f => f.userGroupId === group.id);

        const handleOpenViewMembers = () => {
            ShowDialog(ManageTeamFolderMembersDialog, {
                folderId: folder.id
            });
        };

        const handleUpdatePermissions = async (update: "edit" | "view") => {
            try {
                await withProgressDialog({
                    title: t("Updating permissions..."),
                    action: () => folder
                        ? PresentationPermissionsController.updatePresentationPermissionsForFolder({
                            folderId: folder.id,
                            presentationPermissions: {
                                write: { object: update === "edit", permissions: update === "edit" },
                                read: { object: true, permissions: true }
                            }
                        })
                        : PresentationPermissionsController.updatePresentationPermissionsForUserGroups({
                            groups: [{
                                id: group.id,
                                permissions: {
                                    owner: false,
                                    write: { object: update === "edit", permissions: update === "edit" },
                                    read: { object: true, permissions: true }
                                }
                            }]
                        })
                });
            } catch (err) {
                logger.error(err, "Failed to update permissions");

                ShowErrorDialog({
                    title: t("Failed to update permissions"),
                    message: err.message
                });
            }
        };

        const handleRemovePermissions = async () => {
            try {
                await withProgressDialog({
                    title: folder ? t("Removing presentation from folder...") : t("Revoking access..."),
                    action: () => folder
                        ? PresentationPermissionsController.removePresentationPermissionsForFolder({ folderId: folder.id })
                        : PresentationPermissionsController.removePresentationPermissionsForUserGroups({ groups: [{ id: group.id }] })
                });
            } catch (err) {
                logger.error(err, "Failed to remove presentation from folder");

                ShowErrorDialog({
                    title: folder ? t("Failed to remove presentation from folder") : t("Failed to revoke access"),
                    message: err.message
                });
            }
        };

        const handlePermissionDropdownChange = (update: "edit" | "view" | "remove") => {
            if (update === "remove") {
                handleRemovePermissions();
            } else {
                handleUpdatePermissions(update);
            }
        };

        return (<PermissionRowContainer data-test-id={folder ? "presentation-permissions-list-row-folder" : "presentation-permissions-list-row-group"}>
            <FlexBox left>
                {folder ? <FolderAvatar /> : <UserGroupAvatar type={group.type} name={group.name} />}
                <PermissionRowNameContainer data-test-id="name">
                    <span className="displayName">{group.name}</span>
                </PermissionRowNameContainer>
            </FlexBox>
            <FlexBox left>
                <Dropdown
                    value={permission.write.object ? "edit" : "view"}
                    variant="standard"
                    disableUnderline
                    onChange={handlePermissionDropdownChange}
                    data-test-id="permission-dropdown"
                >
                    <MenuItem value={"edit"}>{t("Can Edit")}</MenuItem>
                    <MenuItem value={"view"}>{t("Can View")}</MenuItem>
                    <Divider />
                    <MenuItem value={"remove"}>{folder ? t("Remove From Folder") : t("Revoke Access")}</MenuItem>
                </Dropdown>
            </FlexBox>
            <FlexBox left>
                {folder && <OpenManageFolderButton unframed onClick={handleOpenViewMembers}>{t("View members")}</OpenManageFolderButton>}
            </FlexBox>
            <FlexBox></FlexBox>
            <FlexBox></FlexBox>
        </PermissionRowContainer>);
    });

function InviteRow({ invite }: { invite: ICollaborationInvite }) {
    const handleUpdatePermissions = async (update: "edit" | "view") => {
        try {
            await withProgressDialog({
                title: t("Updating invite..."),
                action: () => PresentationPermissionsController.updatePresentationPermissionsForUsers({
                    users: [{
                        uid: invite.inviteeUid,
                        email: invite.inviteeEmail,
                        permissions: {
                            write: { object: update === "edit", permissions: update === "edit" },
                            read: { object: true, permissions: true },
                            owner: false
                        }
                    }]
                })
            });
        } catch (err) {
            logger.error(err, "Failed to update invite");

            ShowErrorDialog({
                title: t("Failed to update invite"),
                message: err.message
            });
        }
    };

    const handleRemovePermissions = async () => {
        try {
            await withProgressDialog({
                title: t("Revoking invite..."),
                action: () => PresentationPermissionsController.removePresentationPermissionsForUsers({
                    users: [{
                        uid: invite.inviteeUid,
                        email: invite.inviteeEmail
                    }]
                })
            });
        } catch (err) {
            logger.error(err, "Failed to remove invite");

            ShowErrorDialog({
                title: t("Failed to remove invite"),
                message: err.message
            });
        }
    };

    const handlePermissionDropdownChange = (update: "edit" | "view" | "remove") => {
        if (update === "remove") {
            handleRemovePermissions();
        } else {
            handleUpdatePermissions(update);
        }
    };

    const handleResendInvite = () => {
        ShowDialog(ResendInviteDialog, { invite });
    };

    return (<PermissionRowContainer data-test-id="presentation-permissions-list-row-invite">
        <FlexBox left>
            <Avatar url={null} />
            <PermissionRowNameContainer data-test-id="name">
                <span className="email">{invite.inviteeEmail}</span>
            </PermissionRowNameContainer>
        </FlexBox>
        <FlexBox left>
            <Dropdown
                value={invite.permissions.write ? "edit" : "view"}
                variant="standard"
                disableUnderline
                onChange={handlePermissionDropdownChange}
                data-test-id="permission-dropdown"
            >
                <MenuItem value={"edit"}>{t("Can Edit")}</MenuItem>
                <MenuItem value={"view"}>{t("Can View")}</MenuItem>
                <Divider />
                <MenuItem value={"remove"}>{t("Revoke Invite")}</MenuItem>
            </Dropdown>
        </FlexBox>
        <FlexBox left>
            <MutedGrayText>{t("Pending signup")}</MutedGrayText>
        </FlexBox>
        <FlexBox></FlexBox>
        <FlexBox right>
            {!app.isConstrained &&
                <ResendInviteButton blue onClick={handleResendInvite}>{t("Resend Invite")}</ResendInviteButton>
            }
        </FlexBox>
    </PermissionRowContainer >);
}

export default WorkspaceController.withInitializedState(PresentationPermissionsController.withInitializedState(PermissionsList));
